import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Edit,
  Plus,
  Trash2,
  Link2,
  Users,
  FileText,
  Calendar,
  Clock,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  useHistoryQuery,
  type ActivityLogEntry,
  type ChangeFilter,
  type FieldChange,
} from '@/hooks/useHistory';

interface Task {
  id: string;
  name: string;
}

interface TaskHistoryProps {
  task: Task;
}

const FILTER_OPTIONS: Array<{ value: ChangeFilter; label: string }> = [
  { value: 'all', label: 'All Changes' },
  { value: 'field', label: 'Field Changes' },
  { value: 'dependency', label: 'Dependencies' },
  { value: 'assignment', label: 'Assignments' },
  { value: 'status', label: 'Status Changes' },
];

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  startDate: 'Start Date',
  finishDate: 'Finish Date',
  duration: 'Duration',
  percentComplete: 'Progress',
  status: 'Status',
  priority: 'Priority',
  constraintType: 'Constraint Type',
  constraintDate: 'Constraint Date',
  deadline: 'Deadline',
  taskType: 'Task Type',
  effortDriven: 'Effort Driven',
  customFields: 'Custom Fields',
};

const ACTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  create: Plus,
  update: Edit,
  delete: Trash2,
};

const ENTITY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  task: FileText,
  dependency: Link2,
  assignment: Users,
  comment: FileText,
};

export function TaskHistory({ task }: TaskHistoryProps) {
  const [filter, setFilter] = useState<ChangeFilter>('all');
  const { data: history, isLoading } = useHistoryQuery(task.id, filter);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  const toggleEntry = (entryId: string) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-40 bg-gray-200 animate-pulse rounded" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-64 bg-gray-200 animate-pulse rounded" />
              <div className="h-3 w-32 bg-gray-100 animate-pulse rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Group history by date
  const groupedHistory = groupByDate(history || []);

  return (
    <div className="space-y-6">
      {/* Filter */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Activity Log
        </h3>
        <Select
          value={filter}
          onValueChange={(v) => setFilter(v as ChangeFilter)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            {FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* History List */}
      {!history || history.length === 0 ? (
        <div className="py-8 text-center border rounded-lg border-dashed">
          <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No activity recorded yet.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedHistory).map(([date, entries]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  {date}
                </span>
              </div>
              <div className="space-y-3 ml-2 border-l-2 border-gray-200 pl-4">
                {entries.map((entry) => (
                  <HistoryEntry
                    key={entry.id}
                    entry={entry}
                    isExpanded={expandedEntries.has(entry.id)}
                    onToggle={() => toggleEntry(entry.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface HistoryEntryProps {
  entry: ActivityLogEntry;
  isExpanded: boolean;
  onToggle: () => void;
}

function HistoryEntry({ entry, isExpanded, onToggle }: HistoryEntryProps) {
  const ActionIcon = ACTION_ICONS[entry.action] || Edit;
  const EntityIcon = ENTITY_ICONS[entry.entityType] || FileText;

  const user = entry.user;
  const displayName = user
    ? user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email
    : 'System';

  const actionText = getActionText(entry);
  const hasDetails = entry.changes && entry.changes.length > 0;

  return (
    <div className="relative">
      {/* Timeline dot */}
      <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-gray-300 border-2 border-white" />

      <div className="bg-gray-50 rounded-lg p-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage src={user?.profilePicture || undefined} />
            <AvatarFallback className="text-xs">
              {getInitials(user?.firstName, user?.lastName, user?.email)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{displayName}</span>
              <Badge
                variant="outline"
                className="flex items-center gap-1 text-xs"
              >
                <ActionIcon className="h-3 w-3" />
                {entry.action}
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                <EntityIcon className="h-3 w-3" />
                {entry.entityType}
              </Badge>
            </div>

            <p className="text-sm text-muted-foreground mt-1">{actionText}</p>

            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(entry.createdAt), {
                addSuffix: true,
              })}
            </span>

            {/* Expandable Details */}
            {hasDetails && (
              <div className="mt-2">
                <button
                  onClick={onToggle}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  {isExpanded ? 'Hide details' : 'Show details'}
                </button>

                {isExpanded && (
                  <div className="mt-2 space-y-2">
                    {entry.changes.map((change, index) => (
                      <ChangeDetail key={index} change={change} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ChangeDetailProps {
  change: FieldChange;
}

function ChangeDetail({ change }: ChangeDetailProps) {
  const fieldLabel = FIELD_LABELS[change.field] || change.field;
  const oldValue = formatValue(change.field, change.oldValue);
  const newValue = formatValue(change.field, change.newValue);

  return (
    <div className="text-xs bg-white border rounded p-2">
      <span className="font-medium text-muted-foreground">{fieldLabel}:</span>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-red-600 line-through">
          {oldValue || '(empty)'}
        </span>
        <span className="text-muted-foreground">→</span>
        <span className="text-green-600">{newValue || '(empty)'}</span>
      </div>
    </div>
  );
}

// Helper functions

function groupByDate(entries: ActivityLogEntry[]): Record<string, ActivityLogEntry[]> {
  const groups: Record<string, ActivityLogEntry[]> = {};

  for (const entry of entries) {
    const date = format(new Date(entry.createdAt), 'MMMM d, yyyy');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(entry);
  }

  return groups;
}

function getActionText(entry: ActivityLogEntry): string {
  const { action, entityType, changes } = entry;

  if (entityType === 'task') {
    if (action === 'create') return 'created this task';
    if (action === 'delete') return 'deleted this task';
    if (action === 'update' && changes.length > 0) {
      const fields = changes.map((c) => FIELD_LABELS[c.field] || c.field);
      if (fields.length === 1) return `updated ${fields[0].toLowerCase()}`;
      if (fields.length === 2) return `updated ${fields[0].toLowerCase()} and ${fields[1].toLowerCase()}`;
      return `updated ${fields.length} fields`;
    }
    return 'made changes';
  }

  if (entityType === 'dependency') {
    if (action === 'create') return 'added a dependency';
    if (action === 'delete') return 'removed a dependency';
    if (action === 'update') return 'modified a dependency';
  }

  if (entityType === 'assignment') {
    if (action === 'create') return 'assigned a resource';
    if (action === 'delete') return 'removed an assignment';
    if (action === 'update') return 'updated an assignment';
  }

  if (entityType === 'comment') {
    if (action === 'create') return 'added a comment';
    if (action === 'delete') return 'deleted a comment';
    if (action === 'update') return 'edited a comment';
  }

  return `${action}d ${entityType}`;
}

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '';

  // Date fields
  if (['startDate', 'finishDate', 'constraintDate', 'deadline'].includes(field)) {
    try {
      return format(new Date(value as string), 'MMM d, yyyy');
    } catch {
      return String(value);
    }
  }

  // Boolean fields
  if (field === 'effortDriven') {
    return value ? 'Yes' : 'No';
  }

  // Percent complete
  if (field === 'percentComplete') {
    return `${value}%`;
  }

  // Duration
  if (field === 'duration') {
    return `${value} days`;
  }

  // Priority
  if (field === 'priority') {
    const priorities: Record<number, string> = {
      100: 'Highest',
      300: 'High',
      500: 'Normal',
      700: 'Low',
      900: 'Lowest',
    };
    return priorities[value as number] || String(value);
  }

  // Object values (like customFields)
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | undefined
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return 'S';
}

export default TaskHistory;
